import { Button, Space, Typography, Form } from "antd";
import { useEffect, useState } from "react";
import {
  addVariable,
  deleteVariable,
  updateVariable,
} from "../repository/data.repository";
import { ContentType, VariableType } from "../repository/data.default";
import { VariableForm } from "./VariablesFormDialog.default";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";
import { getProblemMessage } from "../utils/http";
import { enumRequest } from "../utils/options";
export interface VariablesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: VariableForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function VariablesFormDialog(props: VariablesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<VariableForm>([], form);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [valid, setValid] = useState(false);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) setFormFields(form, props.initialValues);
      else form.setFieldsValue(props.initialValues);
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, description, tag, contentType, value } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "delete") {
      deleteVariable(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateVariable({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
          tag,
          contentType: contentType.value,
          value,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch(onError);
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "add") {
      addVariable({
        name,
        description,
        tag,
        contentType: contentType.value,
        value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "delete":
        return "Delete";
    }
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={
        props.initialValues?.variableType !== VariableType.System
          ? modalTitle(props.mode, "Variable", props.initialValues?.name)
          : "Variable"
      }
      onCancel={onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={onCancel}>
            {props.initialValues?.variableType !== VariableType.System
              ? "Cancel"
              : "Close"}
          </Button>
          <>
            {props.initialValues?.variableType !== VariableType.System ? (
              <Button
                type={"primary"}
                danger={props.mode === "delete"}
                loading={loading}
                disabled={!valid && props.mode !== "delete"}
                onClick={onSubmit}
              >
                {createMode()}
              </Button>
            ) : null}
          </>
        </Space>
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          preview={
            props.initialValues?.variableType === VariableType.System
              ? true
              : false
          }
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: true,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                    rules: [
                      {
                        required: true,
                        message: "Description is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "tag",
                    label: "Tag",
                    rules: [
                      {
                        required: true,
                        message: "Tag is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "contentType",
                    label: "Content Type",
                    rules: [
                      {
                        required: true,
                        message: "Content Type is required",
                      },
                    ],
                    request: enumRequest(ContentType),
                  },
                  {
                    type: "textbox",
                    name: "value",
                    label: "Value",
                    rules: [
                      {
                        required: true,
                        message: "Content Type is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" &&
        props.initialValues?.variableType === VariableType.Custom && (
          <ModalDeleteConfirmation
            entityLabel="variable"
            entityName={props.initialValues?.name}
          />
        )}

      {props.mode === "delete" &&
        props.initialValues?.variableType === VariableType.System && (
          <Typography.Paragraph>
            <Typography.Text>
              Can't {props.mode} the{" "}
              <Typography.Text className="font-bold">
                {props.initialValues?.name}
              </Typography.Text>{" "}
              System type variable, you can {props.mode}
              <Typography.Paragraph>
                only Custom type variables.
              </Typography.Paragraph>
            </Typography.Text>
          </Typography.Paragraph>
        )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default VariablesFormDialog;
